// Long AI jobs that run on the server in the background: the literacy plan (~60 s) and the
// survey analysis (~30 s). POST starts the job ({status: "running"}), GET /literacy and GET /survey
// report plan_status / analysis_status. This provider polls every 3 s while a job runs, keeps
// running across page changes inside /app, and shows a toast with "Odpri" when a job finishes
// while the person is on another page. Older servers answer the POST with the finished result;
// that works too (the job is then "ready" as soon as the POST returns).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { api } from "../api";
import type { JobStatus, LiteracyState, SurveyState } from "../types";
import { t } from "../i18n/sl";
import { errorMessage, useToast } from "../components/ui";
import { useSession } from "./session";

export type JobKind = "plan" | "analysis";

interface JobInfo {
  status: JobStatus;
  /** epoch ms; known when this browser started the job */
  startedAt?: number;
  error?: string;
  /** increases each time the job finishes, so pages know to reload */
  version: number;
}

const POLL_MS = 3000;

const META: Record<JobKind, { href: string; ready: string }> = {
  plan: { href: "/app/pismenost", ready: t.pismenost.planReady },
  analysis: { href: "/app/anketa", ready: t.anketa.analysisReady },
};

function statusOf(kind: JobKind, data: LiteracyState | SurveyState): { status?: JobStatus; error?: string } {
  if (kind === "plan") {
    const d = data as LiteracyState;
    return { status: d.plan_status, error: d.plan_error ?? undefined };
  }
  const d = data as SurveyState;
  return { status: d.analysis_status, error: d.analysis_error ?? undefined };
}

function fetchState(kind: JobKind): Promise<LiteracyState | SurveyState> {
  return kind === "plan" ? api.literacy() : api.survey();
}

/** A POST answer is a "job started" note when it has a status and no result fields. */
function isStarted(r: unknown): boolean {
  return !!r && typeof r === "object" && "status" in r && !("roles" in r) && !("summary" in r);
}

interface JobsCtx {
  jobs: Record<JobKind, JobInfo>;
  start: (kind: JobKind) => Promise<void>;
  /** A page read the server state: sync the job with it (starts polling when it runs). */
  observe: (kind: JobKind, status: JobStatus | undefined, error?: string | null) => void;
}

const Ctx = createContext<JobsCtx | null>(null);

const storeKey = (orgId: string) => `semafor_jobs_v1:${orgId}`;

function loadStarted(orgId: string): Partial<Record<JobKind, number>> {
  try {
    return JSON.parse(localStorage.getItem(storeKey(orgId)) ?? "{}");
  } catch {
    return {};
  }
}

export function JobsProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const orgId = me?.org?.id ?? "none";
  const toast = useToast();
  const navigate = useNavigate();
  const loc = useLocation();
  const locRef = useRef(loc.pathname);
  locRef.current = loc.pathname;

  const [jobs, setJobs] = useState<Record<JobKind, JobInfo>>(() => {
    const started = loadStarted(orgId);
    const mk = (k: JobKind): JobInfo =>
      started[k] ? { status: "running", startedAt: started[k], version: 0 } : { status: "none", version: 0 };
    return { plan: mk("plan"), analysis: mk("analysis") };
  });
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;

  const persist = useCallback(
    (kind: JobKind, startedAt: number | null) => {
      const cur = loadStarted(orgId);
      if (startedAt) cur[kind] = startedAt;
      else delete cur[kind];
      localStorage.setItem(storeKey(orgId), JSON.stringify(cur));
    },
    [orgId],
  );

  const finish = useCallback(
    (kind: JobKind, status: "ready" | "error" | "none", error?: string) => {
      const wasRunning = jobsRef.current[kind].status === "running";
      persist(kind, null);
      setJobs((j) => ({
        ...j,
        [kind]: { status, error, version: j[kind].version + (status === "ready" ? 1 : 0) },
      }));
      if (!wasRunning) return;
      const away = !locRef.current.startsWith(META[kind].href);
      if (status === "ready" && away) {
        toast(META[kind].ready, "ok", {
          action: { label: t.common.open, onClick: () => navigate(META[kind].href) },
        });
      } else if (status === "error" && away) {
        toast(error || t.jobs.failed, "error", {
          action: { label: t.common.open, onClick: () => navigate(META[kind].href) },
        });
      }
    },
    [navigate, persist, toast],
  );

  // poll every 3 s while a job runs
  const running = (Object.keys(jobs) as JobKind[]).filter((k) => jobs[k].status === "running");
  const runningKey = running.join(",");
  useEffect(() => {
    if (!runningKey) return;
    const kinds = runningKey.split(",") as JobKind[];
    let alive = true;
    const tick = async () => {
      for (const kind of kinds) {
        try {
          const data = await fetchState(kind);
          if (!alive) return;
          const { status: st, error } = statusOf(kind, data);
          if (st === "ready") finish(kind, "ready");
          else if (st === "error") finish(kind, "error", error);
          else if (st === "none") finish(kind, "none");
          // undefined: an older server without background jobs; the POST answer decides
        } catch {
          /* network blip: try again on the next tick */
        }
      }
    };
    const id = window.setInterval(tick, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [runningKey, finish]);

  const start = useCallback(
    async (kind: JobKind) => {
      const startedAt = Date.now();
      persist(kind, startedAt);
      setJobs((j) => ({ ...j, [kind]: { status: "running", startedAt, version: j[kind].version } }));
      try {
        const r = kind === "plan" ? await api.literacyPlan() : await api.surveyAnalyze();
        if (!isStarted(r)) finish(kind, "ready"); // older server: the result is already stored
      } catch (e) {
        persist(kind, null);
        setJobs((j) => ({ ...j, [kind]: { status: "error", error: errorMessage(e), version: j[kind].version } }));
      }
    },
    [finish, persist],
  );

  const observe = useCallback(
    (kind: JobKind, status: JobStatus | undefined, error?: string | null) => {
      if (!status) return;
      const cur = jobsRef.current[kind];
      if (status === "running" && cur.status !== "running") {
        const startedAt = loadStarted(orgId)[kind] ?? Date.now();
        persist(kind, startedAt);
        setJobs((j) => ({ ...j, [kind]: { status: "running", startedAt, version: j[kind].version } }));
      } else if (status !== "running" && cur.status === "running") {
        // the page already holds the fresh data, so no reload and no toast
        persist(kind, null);
        setJobs((j) => ({ ...j, [kind]: { status, error: error ?? undefined, version: j[kind].version } }));
      } else if (status === "error" && cur.status !== "error") {
        setJobs((j) => ({ ...j, [kind]: { ...j[kind], status, error: error ?? undefined } }));
      }
    },
    [orgId, persist],
  );

  const value = useMemo(() => ({ jobs, start, observe }), [jobs, start, observe]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useJob(kind: JobKind) {
  const c = useContext(Ctx);
  if (!c) throw new Error("useJob outside JobsProvider");
  const job = c.jobs[kind];
  const { start, observe } = c;
  return {
    ...job,
    running: job.status === "running",
    start: useCallback(() => start(kind), [start, kind]),
    observe: useCallback((s: JobStatus | undefined, e?: string | null) => observe(kind, s, e), [observe, kind]),
  };
}
